import { useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { useDispatch } from 'react-redux';
import { CallManagerProvider } from '../call';
import { useUser } from '../hooks/useUser';
import { useSignOut } from '../hooks/useAuth';
import { useCallPresenceHeartbeat } from '../hooks/useCallPresenceHeartbeat';
import { resetUser, updateRolePresence } from '../store/userReducer';
import DashboardHeader from '../components/dashboard/DashboardHeader';
import DashboardFooter from '../components/dashboard/DashboardFooter';
import NotificationsPopover from '../components/dashboard/NotificationsPopover';
import { subscribeApiHealth } from '../services/api';
import { listNotifications, markNotificationAsSeen } from '../services/notifications';
import { getCallableUsers } from '../services/call';
import { updateUserPresence } from '../services/users';
import { APP_ROLE, resolveAppRole } from '../permissions';
import {
    buildMissedCallRemoteUser,
    enrichNotificationsForDisplay,
    resolveAlertNotificationTarget,
    resolveAppointmentNotificationTarget
} from '../utils/notificationActions';

const dialogPaperSx = {
    borderRadius: '22px',
    boxShadow: '0px 10px 30px rgba(61, 61, 61, 0.12)'
};

const dialogTitleSx = {
    px: 3,
    pt: 3,
    pb: 1.5,
    fontWeight: 700,
    color: '#3D3D3D'
};

const dialogContentSx = {
    px: 3,
    pb: 1,
    pt: 0.5
};

const dialogActionsSx = {
    px: 3,
    pb: 2.5,
    pt: 1,
    gap: 1
};

const secondaryDialogButtonSx = {
    minHeight: 48,
    px: 2.2,
    textTransform: 'none',
    fontWeight: 700,
    color: '#3D3D3D',
    borderColor: '#D9D9D9',
    borderWidth: '2px',
    borderRadius: '16px',
    '&:hover': {
        bgcolor: '#F7F7F7',
        borderColor: '#CFCFCF',
        borderWidth: '2px'
    }
};

const primaryDialogButtonSx = {
    minHeight: 48,
    px: 2.2,
    textTransform: 'none',
    fontWeight: 700,
    borderRadius: '16px',
    bgcolor: '#D63731',
    boxShadow: 'none',
    '&:hover': {
        bgcolor: '#D63731',
        boxShadow: 'none'
    }
};

const AppLayout = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const { user } = useUser();
    const logout = useSignOut();
    const [confirmLogoutOpen, setConfirmLogoutOpen] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [notificationsLoading, setNotificationsLoading] = useState(false);
    const [notificationsAnchorEl, setNotificationsAnchorEl] = useState(null);
    const [notificationToConfirm, setNotificationToConfirm] = useState(null);
    const [missedCallDialogUser, setMissedCallDialogUser] = useState(null);
    const [notificationNavigationError, setNotificationNavigationError] = useState('');
    const [callableUsers, setCallableUsers] = useState([]);
    const [notificationsPageIndex, setNotificationsPageIndex] = useState(0);
    const [notificationsPageTokens, setNotificationsPageTokens] = useState({ 0: null });
    const [notificationsTotalCount, setNotificationsTotalCount] = useState(0);
    const [hasNextNotificationsPage, setHasNextNotificationsPage] = useState(false);
    const [hasNewNotificationArrival, setHasNewNotificationArrival] = useState(false);
    const [presenceDialogOpen, setPresenceDialogOpen] = useState(false);
    const [presenceUpdating, setPresenceUpdating] = useState(false);
    const [presenceError, setPresenceError] = useState('');
    const [isOffline, setIsOffline] = useState(() => !window.navigator.onLine);
    const [isServerUnavailable, setIsServerUnavailable] = useState(false);
    const [signalingServiceStatus, setSignalingServiceStatus] = useState({
        phase: 'initializing',
        message: ''
    });
    const latestTopNotificationUuidRef = useRef(null);
    const hasInitializedNotificationsRef = useRef(false);
    const notificationAudioRef = useRef(null);
    const selectedRole = user.roles?.find((role) => role.uuid === user.selectedRole) || null;
    const selectedRoleName = selectedRole?.name || null;
    const selectedRoleType = selectedRole?.role || null;
    const displayName = selectedRoleName || user.name || user.email || 'Utilizador';
    const canAccessSettings = resolveAppRole(selectedRoleType) === APP_ROLE.ACCOUNT_ADMIN;
    const canManageCallAvailability = [APP_ROLE.ACCOUNT_ADMIN, APP_ROLE.CLINIC_CARE_GIVER].includes(
        resolveAppRole(selectedRoleType)
    );
    const isAvailableForCaregiverCalls = selectedRole?.presence_state !== 'AWAY';
    const shouldShowCallAvailabilityBanner =
        canManageCallAvailability && !isAvailableForCaregiverCalls;
    const isCallRoute = location.pathname.startsWith('/call/');
    const currentNotificationsPageToken = notificationsPageTokens[notificationsPageIndex] || null;
    const shouldShowConnectivityBanner = isOffline || isServerUnavailable;
    const connectivityBannerLabel = isOffline ? 'Sem Ligação à Rede' : 'Sem ligação ao servidor';
    const shouldShowSignalingBanner =
        !shouldShowConnectivityBanner &&
        ['recovering', 'unavailable'].includes(signalingServiceStatus.phase);
    const contentTopPadding = useMemo(() => {
        const basePadding = { xs: 76, md: 82 };
        const offlineBannerHeight = 38;
        const signalingBannerHeight = 38;
        const callAvailabilityBannerHeight = 36;
        const extraBannerHeight =
            (shouldShowConnectivityBanner ? offlineBannerHeight : 0) +
            (shouldShowSignalingBanner ? signalingBannerHeight : 0) +
            (shouldShowCallAvailabilityBanner ? callAvailabilityBannerHeight : 0);

        if (extraBannerHeight === 0) {
            return { xs: `${basePadding.xs}px`, md: `${basePadding.md}px` };
        }

        return {
            xs: `${basePadding.xs + extraBannerHeight}px`,
            md: `${basePadding.md + extraBannerHeight}px`
        };
    }, [shouldShowCallAvailabilityBanner, shouldShowConnectivityBanner, shouldShowSignalingBanner]);
    const notificationsTotalPages = useMemo(() => {
        if (notificationsTotalCount <= 0) {
            return 1;
        }

        return Math.max(1, Math.ceil(notificationsTotalCount / 5));
    }, [notificationsTotalCount]);
    useCallPresenceHeartbeat(user.selectedRole);

    useEffect(() => {
        const audio = new Audio('/assets/sounds/notification.mp3');
        audio.preload = 'auto';
        notificationAudioRef.current = audio;

        return () => {
            if (notificationAudioRef.current) {
                notificationAudioRef.current.pause();
                notificationAudioRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        const handleOnline = () => setIsOffline(false);
        const handleOffline = () => setIsOffline(true);

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    useEffect(() => {
        const unsubscribe = subscribeApiHealth((isReachable) => {
            setIsServerUnavailable(!isReachable);
        });

        return unsubscribe;
    }, []);

    useEffect(() => {
        let cancelled = false;
        let intervalId = null;

        const loadNotifications = async ({ silent = false } = {}) => {
            if (!user.selectedRole) {
                if (!cancelled) {
                    setNotifications([]);
                }
                return;
            }

            if (!silent && !cancelled) {
                setNotificationsLoading(true);
            }

            try {
                const response = await listNotifications({
                    userRoleUuid: user.selectedRole,
                    paginationToken: currentNotificationsPageToken,
                    paginationLimit: 5
                });

                if (!cancelled) {
                    const rows = Array.isArray(response?.data) ? response.data : [];
                    const totalCount = Number(response?.headers?.['pagination-count'] || 0);
                    setNotificationsTotalCount(totalCount);
                    const enrichedRows = await enrichNotificationsForDisplay({
                        notifications: rows,
                        userRoleUuid: user.selectedRole
                    });
                    setNotifications(enrichedRows);

                    if (notificationsPageIndex === 0) {
                        const topNotificationUuid = enrichedRows[0]?.uuid || null;
                        const previousTopNotificationUuid = latestTopNotificationUuidRef.current;

                        if (!hasInitializedNotificationsRef.current) {
                            latestTopNotificationUuidRef.current = topNotificationUuid;
                            hasInitializedNotificationsRef.current = true;
                        } else if (topNotificationUuid && previousTopNotificationUuid !== topNotificationUuid) {
                            latestTopNotificationUuidRef.current = topNotificationUuid;
                            setHasNewNotificationArrival(true);

                            if (notificationAudioRef.current) {
                                try {
                                    notificationAudioRef.current.currentTime = 0;
                                    void notificationAudioRef.current.play();
                                } catch {
                                    // Ignore autoplay restrictions silently.
                                }
                            }
                        } else if (!previousTopNotificationUuid && topNotificationUuid) {
                            latestTopNotificationUuidRef.current = topNotificationUuid;
                        }
                    }

                    const nextToken = response?.headers?.['pagination-token'] || null;
                    const nextPageStart = (notificationsPageIndex + 1) * 5;
                    setHasNextNotificationsPage(Boolean(nextToken) && nextPageStart < totalCount);
                    if (nextToken) {
                        setNotificationsPageTokens((prev) => ({
                            ...prev,
                            [notificationsPageIndex + 1]: nextToken
                        }));
                    }
                }
            } catch {
                if (!cancelled) {
                    setNotifications([]);
                    setNotificationsTotalCount(0);
                    setHasNextNotificationsPage(false);
                }
            } finally {
                if (!cancelled) {
                    setNotificationsLoading(false);
                }
            }
        };

        void loadNotifications();
        intervalId = setInterval(() => {
            void loadNotifications({ silent: true });
        }, 30000);

        return () => {
            cancelled = true;
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [currentNotificationsPageToken, notificationsPageIndex, user.selectedRole]);

    useEffect(() => {
        setNotificationsPageIndex(0);
        setNotificationsPageTokens({ 0: null });
        setNotificationsTotalCount(0);
        setHasNextNotificationsPage(false);
        setHasNewNotificationArrival(false);
        latestTopNotificationUuidRef.current = null;
        hasInitializedNotificationsRef.current = false;
    }, [user.selectedRole]);

    useEffect(() => {
        let cancelled = false;
        let intervalId = null;

        const loadCallableUsers = async () => {
            if (!user.selectedRole) {
                if (!cancelled) {
                    setCallableUsers([]);
                }
                return;
            }

            try {
                const response = await getCallableUsers({
                    currentUserId: user.selectedRole,
                    userRoleUuid: user.selectedRole
                });

                if (!cancelled) {
                    setCallableUsers(response.users || []);
                }
            } catch {
                if (!cancelled) {
                    setCallableUsers([]);
                }
            }
        };

        void loadCallableUsers();
        intervalId = setInterval(() => {
            void loadCallableUsers();
        }, 30000);

        return () => {
            cancelled = true;
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [user.selectedRole]);

    const missedCallDialogResolvedUser = missedCallDialogUser?.userId
        ? callableUsers.find((item) => item.userId === missedCallDialogUser.userId) || null
        : null;
    const canCallMissedUser = Boolean(missedCallDialogResolvedUser?.isOnline);

    const handleOpenLogoutDialog = () => {
        setConfirmLogoutOpen(true);
    };

    const handleCloseLogoutDialog = () => {
        setConfirmLogoutOpen(false);
    };

    const handleOpenPresenceDialog = () => {
        setPresenceError('');
        setPresenceDialogOpen(true);
    };

    const handleClosePresenceDialog = () => {
        if (!presenceUpdating) {
            setPresenceDialogOpen(false);
            setPresenceError('');
        }
    };

    const handleConfirmPresenceChange = async () => {
        if (!user.selectedRole) {
            return;
        }

        const nextState = isAvailableForCaregiverCalls ? 'AWAY' : 'ONLINE';
        setPresenceUpdating(true);
        setPresenceError('');

        try {
            await updateUserPresence({
                userRoleUuid: user.selectedRole,
                state: nextState
            });
            dispatch(
                updateRolePresence({
                    roleUuid: user.selectedRole,
                    presenceState: nextState
                })
            );
            setPresenceDialogOpen(false);
        } catch (error) {
            setPresenceError(
                error?.response?.data?.description || 'Não foi possível atualizar a disponibilidade para chamadas.'
            );
        } finally {
            setPresenceUpdating(false);
        }
    };

    const handleOpenNotifications = (event) => {
        setHasNewNotificationArrival(false);
        setNotificationsAnchorEl(event.currentTarget);
    };

    const handleCloseNotifications = () => {
        setNotificationsAnchorEl(null);
    };

    const handleMarkNotificationAsSeen = async (notification) => {
        if (!notification?.uuid) {
            return;
        }

        setNotificationToConfirm(notification);
    };

    const handleNotificationClick = async (notification) => {
        const type = (notification?.type || '').toUpperCase();

        if (!notification || !user.selectedRole) {
            return;
        }

        handleCloseNotifications();

        if (type === 'MISSED_CALL') {
            const remoteUser = buildMissedCallRemoteUser(notification);
            if (remoteUser) {
                setMissedCallDialogUser(remoteUser);
            }
            return;
        }

        if (type === 'APPT_REMINDER') {
            try {
                const target = await resolveAppointmentNotificationTarget({
                    notification,
                    userRoleUuid: user.selectedRole
                });

                if (target?.navigateTo) {
                    navigate(target.navigateTo, {
                        state: target.navigationState
                    });
                }
            } catch {
                // keep silent for now
            }
            return;
        }

        if (type === 'ALERT') {
            try {
                const target = await resolveAlertNotificationTarget({
                    notification,
                    userRoleUuid: user.selectedRole
                });

                if (target?.caregiverRoleUuid) {
                    navigate(
                        `/caregivers/${target.caregiverRoleUuid}?tab=monitorizacao&metric=${target.measurementType}`
                    );
                    return;
                }

                if (target?.unresolvedPatientCaregiver) {
                    setNotificationNavigationError(
                        'Este alerta está associado a um paciente e ainda não foi possível abrir automaticamente o cuidador relacionado neste ambiente.'
                    );
                }
            } catch {
                setNotificationNavigationError(
                    'Não foi possível abrir automaticamente esta notificação.'
                );
            }
        }
    };

    const handleStartMissedCall = () => {
        if (!missedCallDialogUser?.userId) {
            setMissedCallDialogUser(null);
            return;
        }

        navigate(`/call/${missedCallDialogUser.userId}`, {
            state: {
                autoDial: true,
                user: missedCallDialogUser
            }
        });
        setMissedCallDialogUser(null);
    };

    const handleConfirmMarkNotificationAsSeen = async () => {
        if (!notificationToConfirm?.uuid || !user.selectedRole) {
            setNotificationToConfirm(null);
            return;
        }

        try {
            await markNotificationAsSeen({
                userRoleUuid: user.selectedRole,
                notificationUuid: notificationToConfirm.uuid
            });

            setNotificationToConfirm(null);
            setNotificationsPageIndex(0);
            setNotificationsPageTokens({ 0: null });
            setHasNextNotificationsPage(false);
        } catch {
            // keep silent for now; user can retry
            setNotificationToConfirm(null);
        }
    };

    const handleConfirmLogout = async () => {
        setConfirmLogoutOpen(false);
        dispatch(resetUser());
        await logout();
    };

    return (
        <CallManagerProvider onSignalingStatusChange={setSignalingServiceStatus}>
            <Box
                sx={{
                    minHeight: '100vh',
                    bgcolor: '#FFFFFF',
                    display: 'flex',
                    flexDirection: 'column'
                }}
            >
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        zIndex: (theme) => theme.zIndex.appBar,
                        px: 0,
                        pt: 0,
                        pb: 0
                    }}
                >
                    <DashboardHeader
                        userName={displayName}
                        onLogout={handleOpenLogoutDialog}
                        onBrandClick={() => navigate('/')}
                        onNotificationsClick={handleOpenNotifications}
                        hasUnreadNotifications={notifications.length > 0}
                        hasNewNotificationArrival={hasNewNotificationArrival}
                        notificationsOpen={Boolean(notificationsAnchorEl)}
                        canAccessSettings={canAccessSettings}
                        onSettingsClick={() => navigate('/settings')}
                        canManageCallAvailability={canManageCallAvailability}
                        isAvailableForCaregiverCalls={isAvailableForCaregiverCalls}
                        callAvailabilityLoading={presenceUpdating}
                        onCallAvailabilityClick={handleOpenPresenceDialog}
                        onProfileClick={() => navigate('/myprofile')}
                    />
                    {shouldShowConnectivityBanner ? (
                        <Box
                            role="status"
                            aria-live="polite"
                            sx={{
                                height: 38,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: '#D96861',
                                color: '#FFFFFF',
                                borderTop: '1px solid rgba(255,255,255,0.18)',
                                boxShadow: '0px 4px 12px rgba(217, 104, 97, 0.18)'
                            }}
                        >
                            <Typography
                                variant="body1"
                                sx={{
                                    fontWeight: 700,
                                    letterSpacing: '0.01em'
                                }}
                            >
                                {connectivityBannerLabel}
                            </Typography>
                        </Box>
                    ) : null}
                    {shouldShowSignalingBanner ? (
                        <Box
                            role="status"
                            aria-live="polite"
                            sx={{
                                minHeight: 38,
                                px: 2,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 1,
                                bgcolor: '#FFF7E8',
                                color: '#7A4B00',
                                borderBottom: '1px solid #F2D49A'
                            }}
                        >
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                {signalingServiceStatus.message}
                            </Typography>
                            <Button
                                variant="text"
                                onClick={signalingServiceStatus.retry}
                                sx={{
                                    minWidth: 0,
                                    p: 0,
                                    color: '#A66000',
                                    fontWeight: 800,
                                    textTransform: 'none',
                                    textDecoration: 'underline',
                                    textUnderlineOffset: '2px',
                                    '&:hover': {
                                        bgcolor: 'transparent',
                                        color: '#7A4B00'
                                    }
                                }}
                            >
                                Tentar novamente
                            </Button>
                        </Box>
                    ) : null}
                    {shouldShowCallAvailabilityBanner ? (
                        <Box
                            sx={{
                                minHeight: 36,
                                px: 2,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 1,
                                bgcolor: '#FFF5F5',
                                color: '#8F2A26',
                                borderBottom: '1px solid #F1C9C7'
                            }}
                        >
                            <Box
                                component="img"
                                src="/assets/logos-svg/callStatus.svg"
                                alt=""
                                sx={{ width: 18, height: 'auto', opacity: 0.9 }}
                            />
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                As chamadas de cuidadores estão desativadas.
                            </Typography>
                            <Button
                                variant="text"
                                onClick={handleOpenPresenceDialog}
                                disabled={presenceUpdating}
                                sx={{
                                    minWidth: 0,
                                    p: 0,
                                    color: '#D63731',
                                    fontWeight: 800,
                                    textTransform: 'none',
                                    textDecoration: 'underline',
                                    textUnderlineOffset: '2px',
                                    '&:hover': {
                                        bgcolor: 'transparent',
                                        color: '#A92622'
                                    }
                                }}
                            >
                                Ativar chamadas
                            </Button>
                        </Box>
                    ) : null}
                </Box>

                <Box
                    sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        pt: contentTopPadding
                    }}
                >
                    <Outlet />
                </Box>

                {!isCallRoute ? (
                    <Box
                        sx={{
                            mt: 'auto',
                            p: 0,
                            pt: 0
                        }}
                    >
                        <DashboardFooter />
                    </Box>
                ) : null}
            </Box>

            <Dialog
                open={confirmLogoutOpen}
                onClose={handleCloseLogoutDialog}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: dialogPaperSx }}
            >
                <DialogTitle sx={dialogTitleSx}>Terminar sessão</DialogTitle>
                <DialogContent sx={dialogContentSx}>
                    <Typography variant="body2">
                        Tem a certeza que quer terminar sessão?
                    </Typography>
                </DialogContent>
                <DialogActions sx={dialogActionsSx}>
                    <Button onClick={handleCloseLogoutDialog} variant="outlined" sx={secondaryDialogButtonSx}>
                        Cancelar
                    </Button>
                    <Button variant="contained" onClick={handleConfirmLogout} sx={primaryDialogButtonSx}>
                        Logout
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={presenceDialogOpen}
                onClose={handleClosePresenceDialog}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: dialogPaperSx }}
            >
                <DialogTitle sx={dialogTitleSx}>
                    {isAvailableForCaregiverCalls
                        ? 'Ficar indisponível para chamadas?'
                        : 'Voltar a receber chamadas?'}
                </DialogTitle>
                <DialogContent sx={dialogContentSx}>
                    <Typography variant="body2">
                        {isAvailableForCaregiverCalls
                            ? 'Deixará de ser sugerido aos cuidadores para chamadas. Para voltar a receber chamadas, terá de ativar novamente esta opção na barra superior.'
                            : 'Voltará a poder ser sugerido aos cuidadores para chamadas de acordo com a sua especialidade e prioridade.'}
                    </Typography>
                    {presenceError ? (
                        <Typography variant="body2" color="error" sx={{ mt: 1.5 }}>
                            {presenceError}
                        </Typography>
                    ) : null}
                </DialogContent>
                <DialogActions sx={dialogActionsSx}>
                    <Button
                        onClick={handleClosePresenceDialog}
                        variant="outlined"
                        sx={secondaryDialogButtonSx}
                        disabled={presenceUpdating}
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleConfirmPresenceChange}
                        sx={primaryDialogButtonSx}
                        disabled={presenceUpdating}
                    >
                        {presenceUpdating
                            ? 'A atualizar...'
                            : isAvailableForCaregiverCalls
                                ? 'Ficar indisponível'
                                : 'Ativar chamadas'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={Boolean(notificationToConfirm)}
                onClose={() => setNotificationToConfirm(null)}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: dialogPaperSx }}
            >
                <DialogTitle sx={dialogTitleSx}>Marcar notificação como vista</DialogTitle>
                <DialogContent sx={dialogContentSx}>
                    <Typography variant="body2">
                        Tem a certeza que quer marcar esta notificação como vista?
                    </Typography>
                </DialogContent>
                <DialogActions sx={dialogActionsSx}>
                    <Button
                        onClick={() => setNotificationToConfirm(null)}
                        variant="outlined"
                        sx={secondaryDialogButtonSx}
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleConfirmMarkNotificationAsSeen}
                        sx={primaryDialogButtonSx}
                    >
                        Confirmar
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={Boolean(missedCallDialogUser)}
                onClose={() => setMissedCallDialogUser(null)}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: dialogPaperSx }}
            >
                <DialogTitle sx={dialogTitleSx}>Chamada perdida</DialogTitle>
                <DialogContent sx={dialogContentSx}>
                    <Typography variant="body2">
                        Quer ligar para <strong>{missedCallDialogUser?.displayName || 'este utilizador'}</strong>?
                    </Typography>
                    {!canCallMissedUser ? (
                        <Typography variant="body2" sx={{ mt: 1, color: '#6F6F6F' }}>
                            Este utilizador está offline de momento.
                        </Typography>
                    ) : null}
                </DialogContent>
                <DialogActions sx={dialogActionsSx}>
                    <Button
                        onClick={() => setMissedCallDialogUser(null)}
                        variant="outlined"
                        sx={secondaryDialogButtonSx}
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleStartMissedCall}
                        sx={primaryDialogButtonSx}
                        disabled={!canCallMissedUser}
                    >
                        Ligar
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={Boolean(notificationNavigationError)}
                onClose={() => setNotificationNavigationError('')}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: dialogPaperSx }}
            >
                <DialogTitle sx={dialogTitleSx}>Ação indisponível</DialogTitle>
                <DialogContent sx={dialogContentSx}>
                    <Typography variant="body2">
                        {notificationNavigationError}
                    </Typography>
                </DialogContent>
                <DialogActions sx={dialogActionsSx}>
                    <Button
                        variant="contained"
                        onClick={() => setNotificationNavigationError('')}
                        sx={primaryDialogButtonSx}
                    >
                        OK
                    </Button>
                </DialogActions>
            </Dialog>

            <NotificationsPopover
                anchorEl={notificationsAnchorEl}
                open={Boolean(notificationsAnchorEl)}
                notifications={notifications}
                loading={notificationsLoading}
                onClose={handleCloseNotifications}
                onMarkSeen={handleMarkNotificationAsSeen}
                onNotificationClick={handleNotificationClick}
                onPrevPage={() => setNotificationsPageIndex((prev) => Math.max(prev - 1, 0))}
                onNextPage={() => {
                    if (!hasNextNotificationsPage) return;
                    setNotificationsPageIndex((prev) => prev + 1);
                }}
                disablePrev={notificationsPageIndex <= 0}
                disableNext={!hasNextNotificationsPage}
                pageLabel={`${notificationsPageIndex + 1} de ${notificationsTotalPages}`}
            />
        </CallManagerProvider>
    );
};

export default AppLayout;
